import React, { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { Tag } from './types';
import { useCRMContext } from './CRMContext';
import { useLoadedData, useRequestLoad } from '../../hooks/useLoadedData';
import { apiFetch } from '../../api/core';


export interface ICRMTagsContext {
  tags: Tag[];
  tagsMap: Record<string, Tag>;
  createTag: (t: Partial<Tag>) => Promise<Tag>;
  ensureLoaded: () => void;
  reload: () => void;
}

const CRMTagsContext = React.createContext<ICRMTagsContext>({
  tags: [],
  tagsMap: {},
  createTag: t => { throw new Error("createTag not implemented"); },
  ensureLoaded: () => {},
  reload: () => {},
});

export const useCRMTagsContext = () => useContext(CRMTagsContext);

export const useEnsureTagsLoaded = () => {
  const tagsContext = useCRMTagsContext();
  useEffect(() => {
    tagsContext.ensureLoaded();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}


interface Props {
}

export const CRMTagsContextProvider = (props: PropsWithChildren<Props>) => {
  const { children } = props;

  const config = useCRMContext();

  const ensureLoaded = useRequestLoad();
  const allTags = useLoadedData<Tag[]>(`${config.apiPath}/tag`, [], ensureLoaded.isLoadRequested);

  
  const data: ICRMTagsContext = useMemo(() => {
    // Build a quick lookup map by ID
    const tagsMap = allTags.data.reduce((acc, tag) => {
      acc[tag._id.toString()] = tag;
      return acc;
    }, {} as Record<string, Tag>);

    const createTag = async (newTagData: Partial<Tag>) => {
      const response = await apiFetch<Tag>(`${config.apiPath}/tag`, "POST", newTagData);
      
      if (response && response._id) {
        // Optimistically update the loaded data state so the UI reflects the new tag immediately
        allTags.setData(prev => {
          // Prevent duplicates just in case
          if (prev.some(t => t._id === response._id)) return prev;
          
          // Add new tag and sort alphabetically by name (matching your view's order)
          const updatedList = [...prev, response];
          return updatedList.sort((a, b) => a.name.localeCompare(b.name));
        });
        return response;
      }
      throw new Error("Failed to create tag: Invalid response");
    };

    return {
      tags: allTags.data,
      tagsMap,
      createTag,
      ensureLoaded: ensureLoaded.requestLoad,
      reload: () => allTags.reload(),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTags.data, config.apiPath]);


  return (
    <CRMTagsContext.Provider value={data}>
      {children}
    </CRMTagsContext.Provider>
  );
}
